import { useEffect } from 'react'

import { UseFormPayload, useSetState } from '@domain-group/react-hooks'

import { sortArrayBy } from '@domain-group/javascript-utils'

import callApi from '@functions/callApi'

import useAgencySelector from '@hooks/useAgencySelector'
import useIntegration, { UseIntegrationPayload } from '@hooks/useIntegration'
import useIntegrationData, { UseIntegrationDataPayload } from '@hooks/useIntegrationData'

export type Account = { code: number, name: string }

export type Connection = { integration_id: number, tenant_id: string, tenant_name: string }

export interface XeroInvoice {
  campaign_id: number,
  campaign_title: string,
  external_invoice_id: string,
  matched: boolean,
  reference: string,
  status: string,
  synced: boolean,
  xero_invoice_number: number,
}

const defaultState = {
  connections: [] as Connection[],
  expenseAccounts: [] as Account[],
  externalInvoices: [] as XeroInvoice[],
  loadingExternalInvoices: false,
  revenueAccounts: [] as Account[],
  selectedIntegrationId: undefined as number | undefined,
}

type SetState = (state: Partial<typeof defaultState>) => void

export const xeroInvoiceStatuses = [
  { value: 'AUTHORISED', title: 'Authorised (default)' },
  { value: 'DRAFT', title: 'Draft' },
  { value: 'SUBMITTED', title: 'Submitted' },
]

const searchExternalInvoices = async (setState: SetState, filters?: object) => {
  const options = { ...filters }
  setState({ loadingExternalInvoices: true })

  const result = await callApi<XeroInvoice[]>('/invoices/search_external_invoices', options)
  if (result.success) setState({ externalInvoices: result.data })

  setState({ loadingExternalInvoices: false })

  return result
}

const fetchConnections = async (setState: SetState, accountingPlatformApi?: string) => {
  const result = await callApi<Connection[]>(
    '/integrations/connections',
    { accounting_platform_api: accountingPlatformApi || '' },
  )
  if (result.success) setState({ connections: sortArrayBy(result.data, 'asc', 'tenant_name') })

  return result
}

const fetchExpenseAccounts = async (setState: SetState, agencyId: number) => {
  const options = { account_type: 'expense', agency_id: agencyId }

  const result = await callApi<Account[]>('/integrations/accounts', options)
  if (result.success) setState({ expenseAccounts: sortArrayBy(result.data, 'asc', 'name') })

  return result
}

const fetchRevenueAccounts = async (setState: SetState, agencyId: number) => {
  const options = { account_type: 'revenue', agency_id: agencyId }

  const result = await callApi<Account[]>('/integrations/accounts', options)
  if (result.success) setState({ revenueAccounts: sortArrayBy(result.data, 'asc', 'name') })

  return result
}

type IntegrationParams = { integrable_id: number, integrable_type: string }

type DuplicateIntegrationParams = {
  duplicateFn: UseIntegrationPayload['callbacks']['duplicateIntegration'],
  integrationParams: IntegrationParams,
  loadIntegrationData: UseIntegrationDataPayload['callbacks']['loadIntegrationData'],
}

const duplicateIntegration = (params: DuplicateIntegrationParams) => {
  const { duplicateFn, integrationParams, loadIntegrationData } = params

  return duplicateFn(integrationParams).then((result) => {
    if (result.success) loadIntegrationData()

    return result
  })
}

type UseAccountingIntegrationOptions = {
  agencyEntityState?: UseFormPayload['entityState'],
  externalInvoiceFilters?: object,
  shouldFetchAccounts?: boolean,
  shouldFetchConnections?: boolean,
  shouldFetchIntegrationData?: boolean,
  shouldSearchExternalInvoices?: boolean,
}

function useAccountingIntegration(options: UseAccountingIntegrationOptions) {
  const {
    agencyEntityState,
    externalInvoiceFilters,
    shouldFetchAccounts,
    shouldFetchConnections,
    shouldFetchIntegrationData,
    shouldSearchExternalInvoices,
  } = options

  const [state, setState] = useSetState(defaultState)
  const {
    connections, expenseAccounts, externalInvoices, loadingExternalInvoices, revenueAccounts, selectedIntegrationId,
  } = state

  // Selected Agency
  const { selectedAgencyId, selectedAgency } = useAgencySelector()
  const agency = agencyEntityState || selectedAgency
  const accountingPlatformApi: string = selectedAgency?.options?.accounting_platform_api || 'xero'

  // Integration Data
  const integrationDataPayload = useIntegrationData({
    performHttpRequests: !!agency.id && shouldFetchIntegrationData,
    requestOptions: { agency_id: agency?.id || selectedAgency?.id, service_provider: accountingPlatformApi },
  })

  const {
    callbacks: {
      disconnectIntegration,
      loadIntegrationData,
    },
    disconnecting,
    hasActiveSession,
    loading: loadingIntegrationData,
    integrationData,
  } = integrationDataPayload

  // Load Xero Accounts and Connections
  useEffect(() => {
    if (hasActiveSession && shouldFetchAccounts && accountingPlatformApi === 'xero'){
      // @ts-expect-error TS2345
      fetchExpenseAccounts(setState, selectedAgencyId)
      // @ts-expect-error TS2345
      fetchRevenueAccounts(setState, selectedAgencyId)
    }
  }, [hasActiveSession, shouldFetchAccounts, selectedAgencyId])

  useEffect(() => {
    if (shouldFetchConnections){
      fetchConnections(setState, accountingPlatformApi)
    }
  }, [shouldFetchConnections])

  useEffect(() => {
    if (shouldSearchExternalInvoices){
      searchExternalInvoices(setState, externalInvoiceFilters)
    }
  }, [shouldSearchExternalInvoices, JSON.stringify(externalInvoiceFilters)])

  const hasAccountingIntegrationInvoices = !!externalInvoices.length

  const defaultXeroCreditAccount = agency?.options?.default_xero_credit_account || ''
  const defaultXeroSalesAccount = agency?.options?.default_xero_account || '200'
  const defaultXeroInvoiceStatus = agency?.options?.default_xero_invoice_status || 'AUTHORISED'

  const selectedConnection = connections.find(connection => connection.integration_id === selectedIntegrationId)

  const {
    callbacks: { duplicateIntegration: duplicateFn },
    creating,
  } = useIntegration({ id: selectedIntegrationId })

  return {
    accountingPlatformApi,
    authProviderKey: 'xero_oauth2',
    callbacks: {
      disconnectIntegration,
      duplicateIntegration: (integrationParams: IntegrationParams) => (
        duplicateIntegration({ duplicateFn, integrationParams, loadIntegrationData })
      ),
      setState,
    },
    connections,
    creatingIntegration: creating,
    defaultXeroCreditAccount,
    defaultXeroInvoiceStatus,
    defaultXeroSalesAccount,
    disconnecting,
    expenseAccounts,
    externalInvoices,
    hasActiveSession,
    hasRevenueAccountMatch: !!revenueAccounts.find(account => account.code === defaultXeroSalesAccount),
    hasExpenseAccountMatch: !!expenseAccounts.find(account => account.code === defaultXeroCreditAccount),
    hasAccountingIntegrationInvoices,
    integrationData,
    loadingExternalInvoices,
    loadingIntegrationData,
    newIntegrationUrl: `/users/auth/xero_oauth2?agency_id=${selectedAgencyId}`,
    revenueAccounts,
    selectedAgencyId,
    selectedConnection,
    selectedIntegrationId,
  }
}

export type UseAccountingIntegrationPayload = ReturnType<typeof useAccountingIntegration>

export default useAccountingIntegration
